/**
 * 固件上报触发的数据回读节流队列。
 *
 * 固件短时间内反复读同地址会 busy；上报风暴时若每次都立刻 getXxx，
 * 会把未完成的读请求堆进 SDK CommandQueue。本队列保证：
 * 1. 队列空 → 入队并立刻开始
 * 2. 仅 1 个进行中 → 再入队排队（进行中的固件请求无法取消）
 * 3. 已有排队 → 丢掉全部未开始的请求，只保留最新一次
 *
 * 即最多 1 个 in-flight + 1 个 pending。
 *
 * `trailingDelayMs`：in-flight 结束后若还有 pending，先静默等待再跑。
 * 静默期内再次 replace pending 会重置计时——FN 连切时最终只打最新一次回读。
 */
export type CoalesceLatestRequestQueue = {
  enqueue: (task: () => Promise<void>) => void
  clearPending: () => void
  readonly size: number
}

export type CoalesceLatestRequestQueueOptions = {
  /** in-flight 结束后启动 pending 前的静默等待（ms），默认 0 */
  trailingDelayMs?: number
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export function createCoalesceLatestRequestQueue(
  _label = 'coalesceLatestRequest',
  options: CoalesceLatestRequestQueueOptions = {},
): CoalesceLatestRequestQueue {
  const trailingDelayMs = Math.max(0, options.trailingDelayMs ?? 0)
  const queue: Array<() => Promise<void>> = []
  let draining = false
  /** 静默等待代数：pending 被替换时自增，使进行中的 settle 作废并重等 */
  let settleGeneration = 0

  const invalidateSettle = () => {
    if (trailingDelayMs <= 0) return
    settleGeneration++
  }

  /** in-flight 结束后、跑 pending 前：完整静默 trailingDelayMs；期间有新 pending 则重来 */
  const settleBeforePending = async () => {
    if (trailingDelayMs <= 0 || queue.length === 0) return

    for (;;) {
      const gen = settleGeneration
      await delay(trailingDelayMs)
      if (gen === settleGeneration) return
    }
  }

  const drain = async () => {
    if (draining) return
    draining = true
    try {
      let isFirstInThisDrain = true
      while (queue.length > 0) {
        if (!isFirstInThisDrain) {
          await settleBeforePending()
          if (queue.length === 0) break
        }
        isFirstInThisDrain = false

        const task = queue[0]
        try {
          await task()
        } catch (error) {
          console.error(`[${_label}]`, error)
        }
        if (queue[0] === task) {
          queue.shift()
        } else {
          queue.shift()
        }
      }
    } finally {
      draining = false
      if (queue.length > 0) {
        void drain()
      }
    }
  }

  return {
    enqueue(task) {
      if (queue.length === 0) {
        queue.push(task)
        void drain()
        return
      }
      if (queue.length === 1) {
        queue.push(task)
        invalidateSettle()
        return
      }
      queue.splice(1)
      queue.push(task)
      invalidateSettle()
    },
    clearPending() {
      if (queue.length > 1) {
        queue.splice(1)
        invalidateSettle()
      }
    },
    get size() {
      return queue.length
    },
  }
}
